/**
 * Display-only lines for the Internal Review PDF.
 * Reads stored node fields. Does not rewrite commercial totals.
 */

import type {
  DesignNodeData,
  HardwareListGroup,
  HardwareListItem,
  InstallLaborLine,
  InstallNodeData,
  MaterialsNodeData,
  ProposalNodeSet,
  TravelNodeData,
} from '../types';
import {
  listStructureDesignAuditLines,
  type DesignRateInputs,
} from './proposalDesignBreakdown';
import {
  getMaterialsTotalBoardFeet,
  isProductionBoardFeetOverridden,
  resolveProductionBoardFeet,
} from './productionBoardFeet';
import {
  HARDWARE_GROUP_LABEL,
  hardwareGroupTotal,
} from './proposalHardwareCsvParser';
import {
  getInstallLaborLines,
  INSTALL_LABOR_OTHERS_ROLE,
  isBlankLaborLine,
  isMappedInstallLaborPerson,
  isOthersInstallLaborLine,
  laborLineEffectiveQuantity,
  laborLineJobTotal,
} from './proposalInstallLabor';
import { getStructurePostBasesItems } from './proposalStructurePostBases';
import { getStructureDeckingItems } from './proposalStructureDecking';
import {
  defaultHotelNights,
  isHotelNightsAutocalculated,
  listTravelDayPeople,
  travelHotelRooms,
  travelHotelTotal,
  travelPerDiemPersonQuantity,
  travelPerDiemPersonTotal,
  travelPersonDayTotal,
} from './proposalTravelTotals';

export const REVIEW_EM_DASH = '—';

export type ReviewJoineryPerson = {
  userId: string;
  name?: string;
  title?: string;
};

export type ReviewTextLine = {
  key: string;
  label: string;
  detail?: string;
  amount?: number;
  note?: string;
  others?: boolean;
};

const HARDWARE_REVIEW_GROUPS: HardwareListGroup[] = ['pegs', 'steel', 'fasteners', 'other'];

const JOINERY_USER_FIELD: Record<string, keyof DesignNodeData> = {
  'joinery-pm': 'pmUserId',
  'joinery-apm': 'apmUserId',
  'joinery-lead': 'leadDesignerUserId',
  'joinery-support': 'supportDesignerUserId',
};

export function joineryPersonDisplay(
  userId: string | undefined,
  people: ReviewJoineryPerson[] | undefined,
): { name?: string; title?: string } {
  if (!userId || !people?.length) return {};
  const person = people.find((row) => row.userId === userId);
  const name = (person?.name || '').trim();
  if (!name) return {};
  const title = (person?.title || '').trim();
  return {
    name,
    title: title && title.toLowerCase() !== name.toLowerCase() ? title : undefined,
  };
}

export function formatJoineryRoleLabel(
  role: string,
  person: { name?: string; title?: string },
): string {
  const parts = [role];
  if (person.name) parts.push(person.name);
  if (person.title) parts.push(person.title);
  return parts.join(' · ');
}

function othersLabel(role: string | undefined): string {
  const name = (role || '').trim() || INSTALL_LABOR_OTHERS_ROLE;
  return `${name} (unassigned)`;
}

export function reviewShopBoardFeet(
  ns: Pick<ProposalNodeSet, 'production' | 'materials'>,
): number {
  return resolveProductionBoardFeet(
    ns.production,
    getMaterialsTotalBoardFeet(ns.materials),
  );
}

export function reviewProjectBoardFeet(
  nodeSets: Array<Pick<ProposalNodeSet, 'production' | 'materials' | 'quantity' | 'enabled'>>,
): number {
  return nodeSets.reduce((sum, ns) => {
    if (ns.enabled === false) return sum;
    return sum + reviewShopBoardFeet(ns) * (ns.quantity || 1);
  }, 0);
}

export function reviewShopBoardFeetLabel(
  ns: Pick<ProposalNodeSet, 'production' | 'materials'>,
): string {
  const bf = reviewShopBoardFeet(ns);
  const qty = bf.toLocaleString('en-US');
  if (isProductionBoardFeetOverridden(ns.production)) {
    return `${qty} BF (override)`;
  }
  return `${qty} BF`;
}

function enhanceJoineryLabel(
  design: DesignNodeData,
  key: string,
  roleLabel: string,
  people?: ReviewJoineryPerson[],
): string {
  const field = JOINERY_USER_FIELD[key];
  const userId = field ? (design[field] as string | undefined) : undefined;
  return formatJoineryRoleLabel(roleLabel, joineryPersonDisplay(userId, people));
}

export function listJoineryReviewLines(
  design: DesignNodeData,
  rates: DesignRateInputs | null | undefined,
  people?: ReviewJoineryPerson[],
): ReviewTextLine[] {
  if (rates) {
    return listStructureDesignAuditLines(design, rates).map((line) => ({
      key: line.key,
      label: enhanceJoineryLabel(design, line.key, line.label, people),
      detail: line.detail,
      amount: line.amount,
    }));
  }

  const fallback: Array<{ key: string; role: string; hours: number; userId?: string }> = [
    { key: 'joinery-pm', role: 'PM', hours: design.pmHours, userId: design.pmUserId },
    { key: 'joinery-apm', role: 'APM', hours: design.apmHours ?? 0, userId: design.apmUserId },
    { key: 'joinery-lead', role: 'Lead designer', hours: design.leadDesignerHours, userId: design.leadDesignerUserId },
    {
      key: 'joinery-support',
      role: 'Support designer',
      hours: design.supportDesignerHours,
      userId: design.supportDesignerUserId,
    },
  ];

  const lines: ReviewTextLine[] = fallback
    .filter((row) => row.hours > 0)
    .map((row) => ({
      key: row.key,
      label: formatJoineryRoleLabel(row.role, joineryPersonDisplay(row.userId, people)),
      detail: `${row.hours}h`,
    }));

  if (design.engineeringMode === 'lumpSum') {
    const lump = Number(design.engineeringLumpSum) || 0;
    if (lump > 0) {
      lines.push({ key: 'eng-lump', label: 'Engineering', detail: 'Lump sum', amount: lump });
    }
  } else if ((Number(design.engineeringHours) || 0) > 0) {
    lines.push({
      key: 'eng-hourly',
      label: 'Engineering',
      detail: `${design.engineeringHours}h`,
    });
  }

  return lines;
}

function hardwareGroupItems(
  list: HardwareListItem[] | undefined,
  group: HardwareListGroup,
): HardwareListItem[] {
  return (list || []).filter((row) => row.group === group && (Number(row.total) || 0) > 0);
}

export function listMaterialsHardwareReviewLines(
  materials: MaterialsNodeData,
): ReviewTextLine[] {
  const lines: ReviewTextLine[] = [];
  const list = materials.hardwareList;
  const hasList = Array.isArray(list) && list.length > 0;

  for (const group of HARDWARE_REVIEW_GROUPS) {
    if (hasList) {
      const items = hardwareGroupItems(list, group);
      const amount = hardwareGroupTotal(items, group);
      if (!(amount > 0)) continue;
      if (items.length === 1) {
        lines.push({
          key: `hw-${group}`,
          label: (items[0].name || '').trim() || HARDWARE_GROUP_LABEL[group],
          amount,
        });
      } else {
        lines.push({
          key: `hw-${group}`,
          label: HARDWARE_GROUP_LABEL[group],
          detail: `${items.length} items`,
          amount,
        });
      }
      continue;
    }
    if (group === 'pegs' && (materials.pegsCost || 0) > 0) {
      lines.push({ key: 'hw-pegs', label: HARDWARE_GROUP_LABEL.pegs, amount: materials.pegsCost });
    }
    if (group === 'fasteners' && (materials.fastenerPackageCost || 0) > 0) {
      lines.push({
        key: 'hw-fasteners',
        label: HARDWARE_GROUP_LABEL.fasteners,
        amount: materials.fastenerPackageCost,
      });
    }
  }

  getStructurePostBasesItems(materials).forEach((item, index) => {
    if (!((item.total || 0) > 0 || (item.quantity || 0) > 0 || item.productName?.trim())) return;
    const qty = Number(item.quantity) || 0;
    const unit = Number(item.unitCost) || 0;
    lines.push({
      key: `post-base-${item.id || index}`,
      label: item.productName?.trim() || 'Post bases',
      detail: qty > 0 ? `${qty} × $${unit}` : undefined,
      amount: item.total,
    });
  });

  getStructureDeckingItems(materials).forEach((item, index) => {
    if (!((item.total || 0) > 0 || (item.sqFt || 0) > 0 || item.productName?.trim())) return;
    lines.push({
      key: `decking-${item.id || index}`,
      label: item.productName?.trim() || 'Decking',
      detail: (item.sqFt || 0) > 0 ? `${item.sqFt} sq ft` : undefined,
      amount: item.total,
    });
  });

  return lines;
}

export function materialsOtherResidual(
  materials: MaterialsNodeData,
  listedHardware: ReviewTextLine[],
): number {
  const listed = listedHardware.reduce((sum, line) => sum + (Number(line.amount) || 0), 0);
  const other = (Number(materials.total) || 0) - (Number(materials.timberTotal) || 0) - listed;
  return Math.abs(other) < 0.5 ? 0 : other;
}

function laborReviewLabel(line: InstallLaborLine): string {
  if (isOthersInstallLaborLine(line)) return othersLabel(line.role);
  return (line.role || '').trim() || REVIEW_EM_DASH;
}

function laborQtyDetail(line: InstallLaborLine, suffix: string): string {
  const qty = laborLineEffectiveQuantity(line);
  if (isMappedInstallLaborPerson(line)) return suffix;
  return `${qty} × ${suffix}`;
}

export function listInstallLaborReviewLines(install: InstallNodeData): ReviewTextLine[] {
  const days = Number(install.days) || 0;
  return getInstallLaborLines(install)
    .filter((line) => !isBlankLaborLine(line))
    .map((line) => {
      const others = isOthersInstallLaborLine(line);
      const rate = Number(line.ratePerDay) || 0;
      return {
        key: line.id,
        label: laborReviewLabel(line),
        detail: laborQtyDetail(line, `$${rate}/day × ${days}d`),
        amount: laborLineJobTotal(line, days),
        others,
      };
    });
}

export function hotelNightsFormulaNote(
  travel: Pick<TravelNodeData, 'hotelNights' | 'travelDays'>,
  installDays: number,
): string | undefined {
  if (!isHotelNightsAutocalculated(travel.hotelNights, installDays, travel.travelDays)) {
    return undefined;
  }
  const travelDays = Number(travel.travelDays) || 0;
  const onSite = Number(installDays) || 0;
  const auto = defaultHotelNights(onSite, travelDays);
  return `nights = ${onSite} on-site + ${travelDays} travel − 1 (${auto})`;
}

export function listTravelReviewLines(
  travel: TravelNodeData,
  installDays: number,
  laborLines?: InstallLaborLine[],
): ReviewTextLine[] {
  const lines: ReviewTextLine[] = [];
  const rooms = travelHotelRooms(travel);
  const nights = Number(travel.hotelNights) || 0;
  const hotelRate = Number(travel.hotelRatePerNight) || 0;
  if (nights > 0 || hotelRate > 0 || travel.hotelRooms != null) {
    lines.push({
      key: 'hotel',
      label: 'Hotel',
      detail: `${rooms}rm × ${nights}n @ $${hotelRate}`,
      amount: travelHotelTotal(travel),
      note: hotelNightsFormulaNote(travel, installDays),
    });
  }

  const travelDays = Number(travel.travelDays) || 0;
  if (travelDays > 0) {
    lines.push({
      key: 'paid-travel-days',
      label: 'Paid travel days',
      detail: `${travelDays}`,
    });
  }

  const people = listTravelDayPeople(laborLines);
  for (const line of people) {
    const others = isOthersInstallLaborLine(line);
    const rate = Number(line.ratePerDay) || 0;
    lines.push({
      key: `travel-day-${line.id}`,
      label: laborReviewLabel(line),
      detail: laborQtyDetail(line, `$${rate}/day × ${travelDays}d`),
      amount: travelPersonDayTotal(line, travelDays),
      others,
    });
  }

  const perDiemRate = Number(travel.perDiemPerPersonPerDay) || 0;
  if (perDiemRate > 0 && nights > 0) {
    if (people.length > 0) {
      for (const line of people) {
        const others = isOthersInstallLaborLine(line);
        const qty = travelPerDiemPersonQuantity(line, travel);
        const mapped = isMappedInstallLaborPerson(line) && line.id !== 'legacy-crew';
        lines.push({
          key: `per-diem-${line.id}`,
          label: `Per diem · ${laborReviewLabel(line)}`,
          detail: mapped ? `$${perDiemRate}/n × ${nights}n` : `${qty} × $${perDiemRate}/n × ${nights}n`,
          amount: travelPerDiemPersonTotal(line, travel),
          others,
        });
      }
    } else {
      const crew = Number(travel.crewSize) || 0;
      if (crew > 0) {
        lines.push({
          key: 'per-diem',
          label: 'Per diem',
          detail: `${crew} × $${perDiemRate}/n × ${nights}n`,
          amount: crew * perDiemRate * nights,
        });
      }
    }
  }

  const fuel = Number(travel.fuelBudget) || 0;
  if (fuel > 0) {
    lines.push({ key: 'travel-fuel', label: 'Fuel', amount: fuel });
  }

  return lines;
}
